import React, { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { type ConnectedProps, connect } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";
import { change, isDirty } from "redux-form";
import invariant from "tiny-invariant";

import { eraseStateProp } from "~/actions/dashboard";
import { getPatientId } from "~/actions/get_patient_id";
import { type TCreatePatientInstructions, updateAndSubmitPatient, updatePatient } from "~/actions/post_patient";
import { addUserActionNotificationError } from "~/actions/user_notification";
import { isNone } from "~/common/assert";
import { RxTypeId } from "~/common/constants";
import {
  CO_TAG_FULL_3_STEPS_CBCT,
  CO_TAG_TEEN_3_STEPS_CBCT,
  type CourseId,
  getRetainersQuantityFromCourseOptionTag,
  is3DPROCourse,
  isAdditionalAlignersCourse,
  isCapLikeCourse,
  isPowerCapCourse,
  isRetainersCourse,
  Status,
} from "~/common/courses";
import { featureFlags } from "~/common/feature-flags";
import { convertToJSONFullRx, initializeBaseInstruction, mergeCorrectionFormValues } from "~/common/instructions";
import { remoteLog } from "~/common/logging";
import { navigateTo } from "~/common/navigation";
import { assertIsPatient, getLastCorrection, isPatient } from "~/common/patient";
import { Material } from "~/common/prescription";
import { canOrderTestPlastic } from "~/common/user";
import PowerCapFileUpload from "~/components/3d_plan_power_cap/power_cap_file_upload";
import { scrollTo } from "~/components/common/ScrollToElement/scrollTo";
import { MaterialOptions } from "~/components/patient/patient_shared/material_options";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { getCourseInstallment, isCBCTIncludedInCourse, isCBCTServiceAvailableForCourse, selectCourseInstallment } from "~/slices/course-installment";
import { addInstructions, createInstructionsSuccess, selectInstructions, type TInstructions } from "~/slices/instructions";
import { selectAdditionalAlignersFormValues, selectCorrectionFormValues, selectLinksFormValues } from "~/slices/redux-form";
import { selectSmartPrescription } from "~/slices/smart_prescription";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";
import type { TNone } from "~/types/common";
import type { TPatient } from "~/types/patient";

import { PatientUpdateAdditionalAligners } from "./patient_new/patient_new_additional_aligners";
import {
  ADDITIONAL_PAID_SERVICE_TAGS,
  type AdditionalPaidServiceTag,
  isSmartPrescriptionFillingSelected,
  PatientNewAdditionalServices,
  resolveDeepCBCTChecked,
  resolveShowDeepCBCT,
} from "./patient_new/patient_new_additional_services";
import { PatientNewAudioNote } from "./patient_new/patient_new_audio_note";
import { DiagnosisTextArea } from "./patient_new/patient_new_diagnosis";
import { PatientNewImages } from "./patient_new/patient_new_images";
import { type TPatientNewRequiredField, validateReqiredFields, validateRequiredDraftFields } from "./patient_new_validation";
import { SmartPrescriptionCoordinator } from "./patient_shared/smart_prescription_coordinator";
import { TestPlaticOptions } from "./patient_shared/test_plastic_options";
import { PatientUpdateCourse } from "./patient_update/patient_update_course";
import PatientUpdateDoctorInfo from "./patient_update/patient_update_doctor_info";
import { PatientUpdateFiles } from "./patient_update/patient_update_files";
import PatientUpdatePatientInfo from "./patient_update/patient_update_patient_info";
import PatientUpdatePayment from "./patient_update/patient_update_payment";
import PatientUpdateUploadCT from "./patient_update/patient_update_upload_ct";
import RecipeForm from "./recipe_redux_form";
import RouteLeavingGuard from "./route_leaving_guard";

function isAdditionalServiceTag(tag: string): tag is AdditionalPaidServiceTag {
  return ADDITIONAL_PAID_SERVICE_TAGS.includes(tag as AdditionalPaidServiceTag);
}

const PRESCRIPTION_WITH_EXPERT_TAG: AdditionalPaidServiceTag = "PRESCRIPTION_WITH_EXPERT";

export function extractAdditionalServiceTags(patient: TPatient): AdditionalPaidServiceTag[] {
  const topLevelTags = (patient.additional_service_tags ?? []).filter((tag): tag is string => typeof tag === "string");
  const paymentExtraTags = ((patient.course.payments?.extra ?? []) as Array<{ tag?: string }>).map((service) => service.tag).filter((tag): tag is string => typeof tag === "string");
  const services = ((patient as TPatient & { services?: Array<{ tag?: string }> }).services ?? []).map((service) => service.tag).filter((tag): tag is string => typeof tag === "string");

  const tags = [...new Set([...topLevelTags, ...paymentExtraTags, ...services])].filter(isAdditionalServiceTag);
  if (!isSmartPrescriptionFillingSelected(tags) && patient.audio_note) {
    return [...tags, PRESCRIPTION_WITH_EXPERT_TAG];
  }
  return tags;
}

function hasSmartPrescriptionFillingInPatient(patient: TPatient): boolean {
  return isSmartPrescriptionFillingSelected(extractAdditionalServiceTags(patient));
}

export function resolveHasSmartPrescriptionFilling(instructions: Partial<TInstructions>, patient: TPatient): boolean {
  if (Array.isArray(instructions.additional_service_tags)) {
    return isSmartPrescriptionFillingSelected(instructions.additional_service_tags);
  }
  return Boolean(instructions.audio_note) || hasSmartPrescriptionFillingInPatient(patient);
}

function ensureSmartPrescriptionServiceTag(instructions: TCreatePatientInstructions): void {
  if (isSmartPrescriptionFillingSelected(instructions.additional_service_tags)) {
    return;
  }
  instructions.additional_service_tags = Array.from(new Set([...(instructions.additional_service_tags ?? []), PRESCRIPTION_WITH_EXPERT_TAG]));
}

function buildPrescriptionPayload(formValues: PatientUpdateProps["formValues"], hasSmartPrescriptionFilling: boolean, selectedMaterial: number | null) {
  invariant(formValues, "expected formValues to be defined");
  // Смарт-услуга: рецепт заполняется grok'ом в форме — сохраняем значения формы,
  // а не пустой рецепт, иначе заполненное теряется при сохранении. Мерджим дефолты
  // под значения формы: поля без инпутов в форме (напр. correct_proclination_*)
  // обязательны на бэке — иначе update отдаёт 400.
  const prescription = convertToJSONFullRx(mergeCorrectionFormValues(formValues));
  if (hasSmartPrescriptionFilling && prescription.material == null && selectedMaterial != null) {
    prescription.material = selectedMaterial as never;
  }
  return prescription;
}

export const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    patient: state.patient,
    instructions: selectInstructions(state),
    media: state.media,
    media_s3: state.media_s3,
    formValues: selectCorrectionFormValues(state),
    areFormValuesDirty: isDirty("correction")(state),
    additionalAlignersFormValues: selectAdditionalAlignersFormValues(state),
    links: selectLinksFormValues(state),
    areLinksDirty: isDirty("links")(state),
    courseInstallment: selectCourseInstallment(state),
    smartPrescription: selectSmartPrescription(state),
  };
};

export const mapDispatchToProps = {
  updateAndSubmitPatient,
  getPatientId,
  initInstrucrions: createInstructionsSuccess,
  eraseStateProp,
  updatePatient,
  getCourseInstallment,
  addInstructions,
  changeField: change,
  addUserActionNotificationError,
};

// `bareLayout` (own prop): render without the legacy page chrome so the form embeds in the
// redesign-v1 shell. Optional and default-off, so legacy callers are unchanged.
export type PatientUpdateProps = PropsFromRedux & {
  intl: IntlShape;
  bareLayout?: boolean;
} & RouteComponentProps<{
    patient_id: string;
  }>;

type PatientUpdateState = {
  isSubmitPending: boolean;
  isDraftPending: boolean;
  isDirty: boolean;
  submit: boolean;
  selectedMaterial: number | null;
};

export class PatientUpdate extends Component<PatientUpdateProps, PatientUpdateState> {
  constructor(props: PatientUpdateProps) {
    super(props);
    this.state = {
      isSubmitPending: false,
      isDraftPending: false,
      isDirty: false,
      submit: false,
      selectedMaterial: null,
    };
    this.submitButton = this.submitButton.bind(this);
    this.submitSaveButton = this.submitSaveButton.bind(this);
    this.setDirty = this.setDirty.bind(this);
    this.renderRx = this.renderRx.bind(this);
    this.selectAdditionalServiceTags = this.selectAdditionalServiceTags.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_update");
  }

  protected scrollToField(id: TPatientNewRequiredField) {
    scrollTo({ id, duration: 1500 });
  }

  async submitButton() {
    assertIsPatient(this.props.patient);

    const { user, patient, instructions, formValues, additionalAlignersFormValues } = this.props;
    const course_id = instructions.course_id ?? patient.course.course_id;
    const lastCorrection = getLastCorrection(patient);
    const selectedMaterial = this.state.selectedMaterial ?? lastCorrection.prescription?.material ?? null;
    const rxTypeId = RxTypeId.STANDARD;
    const hasSmartPrescriptionFilling = resolveHasSmartPrescriptionFilling(instructions, patient);
    const emptyFields: TPatientNewRequiredField[] = [];

    const linksArray = this.props.links?.links ?? [];
    const links = linksArray.flatMap((link) => (link != null ? Object.values(link) : []));

    const areRequiredFieldsValid = validateReqiredFields({
      user,
      patient,
      links,
      instructions,
      formValues,
      additionalAlignersFormValues,
      media: this.props.media,
      emptyFields,
    });

    const areConditionsSatisfied =
      areRequiredFieldsValid ||
      lastCorrection.index > 0 ||
      ((isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id)) && !lastCorrection.order_options.full_edit) ||
      (lastCorrection.index == 0 && lastCorrection.approved_plan_id);

    if (!areConditionsSatisfied) {
      this.props.addUserActionNotificationError({ message: "notify.patient.required_fields" });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.scrollToField(emptyFields[0]!);
      return;
    }

    this.setState({ isSubmitPending: true, submit: true });

    if (patient.status == Status.UNFILLED_CASE) {
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };
      if (hasSmartPrescriptionFilling) {
        ensureSmartPrescriptionServiceTag(instructionsCopy);
      }

      instructionsCopy.prescription = buildPrescriptionPayload(formValues, hasSmartPrescriptionFilling, selectedMaterial);
      instructionsCopy.prescription.rx_type_id = rxTypeId;
      instructionsCopy.links = links;
      instructionsCopy.additional_aligners = additionalAlignersFormValues?.aligners;

      try {
        await this.props.updateAndSubmitPatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
        this.props.eraseStateProp("patient");
        this.props.history.push("/pages/patients");
      } catch {
        this.setState({ isSubmitPending: false });
      }
    } else if (patient.course.correction.length > 1 && lastCorrection.order_options.can_edit_prescription && lastCorrection.order_options.full_edit) {
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };
      if (hasSmartPrescriptionFilling) {
        ensureSmartPrescriptionServiceTag(instructionsCopy);
      }

      instructionsCopy.prescription = buildPrescriptionPayload(formValues, hasSmartPrescriptionFilling, selectedMaterial);
      instructionsCopy.prescription.rx_type_id = rxTypeId;
      instructionsCopy.links = links;

      try {
        await this.props.updatePatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
        navigateTo(`/pages/patient/${patient.patient_id}`);
      } catch {
        this.setState({ isSubmitPending: false });
      }
    } else if (!lastCorrection.order_options.can_edit_prescription) {
      // Prescription locked: leave it untouched, don't rebuild from `formValues` (no
      // recipe form is shown/editable for this correction).
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };
      instructionsCopy.links = links;

      try {
        await this.props.updatePatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
        navigateTo(`/pages/patient/${patient.patient_id}`);
      } catch {
        this.setState({ isSubmitPending: false });
      }
    } else {
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };
      if (hasSmartPrescriptionFilling) {
        ensureSmartPrescriptionServiceTag(instructionsCopy);
      }

      instructionsCopy.prescription = buildPrescriptionPayload(formValues, hasSmartPrescriptionFilling, selectedMaterial);
      instructionsCopy.prescription.rx_type_id = rxTypeId;
      instructionsCopy.links = links;
      instructionsCopy.additional_aligners = additionalAlignersFormValues?.aligners;

      try {
        await this.props.updatePatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
        navigateTo(`/pages/patient/${patient.patient_id}`);
      } catch {
        this.setState({ isSubmitPending: false });
      }
    }
  }

  async submitSaveButton() {
    assertIsPatient(this.props.patient);

    const { patient, instructions, formValues } = this.props;
    const lastCorrection = getLastCorrection(patient);
    const selectedMaterial = this.state.selectedMaterial ?? lastCorrection.prescription?.material ?? null;
    const rxTypeId = RxTypeId.STANDARD;
    const hasSmartPrescriptionFilling = resolveHasSmartPrescriptionFilling(instructions, patient);
    const emptyFields: TPatientNewRequiredField[] = [];

    const linksArray = this.props.links?.links ?? [];
    const links = linksArray.flatMap((link) => (link != null ? Object.values(link) : []));

    const areRequiredFieldsValid = validateRequiredDraftFields({
      links,
      instructions,
      formValues,
      emptyFields,
    });

    if (!areRequiredFieldsValid) {
      this.props.addUserActionNotificationError({ message: "notify.patient.required_fields" });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.scrollToField(emptyFields[0]!);
      return;
    }

    const instructionsCopy: TCreatePatientInstructions = { ...instructions };
    if (hasSmartPrescriptionFilling) {
      ensureSmartPrescriptionServiceTag(instructionsCopy);
    }

    if (formValues) {
      instructionsCopy.prescription = buildPrescriptionPayload(formValues, hasSmartPrescriptionFilling, selectedMaterial);
      instructionsCopy.prescription.rx_type_id = rxTypeId;
    }
    instructionsCopy.links = links;

    this.setState({ isDraftPending: true, submit: true });

    try {
      await this.props.updatePatient(patient.patient_id, {
        instructions: instructionsCopy,
        media: this.props.media,
        media_s3: this.props.media_s3,
      });
      navigateTo(`/pages/patient/${patient.patient_id}`);
    } catch {
      this.setState({ isDraftPending: false });
    }
  }

  componentDidMount() {
    const { patient_id } = this.props.match.params;
    window.scrollTo(0, 0);

    void this.props.getPatientId(Number(patient_id));
    void this.props.getCourseInstallment(Number(patient_id));

    this.props.initInstrucrions();
    this.props.eraseStateProp("media");

    setDocumentTitle(this.props.intl.formatMessage({ id: "BTN_EDIT" }), patient_id);
  }

  componentDidUpdate(prevProps: PatientUpdateProps) {
    const { submit } = this.state;
    const formIsDirty = !submit && this.getIsFormDirty();

    if (formIsDirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }

    if (isPatient(this.props.patient) && this.props.patient !== prevProps.patient && this.props.instructions.additional_service_tags == null) {
      const tags = extractAdditionalServiceTags(this.props.patient);
      this.props.addInstructions({ additional_service_tags: tags });
    }

    if (isPatient(this.props.patient) && this.props.patient !== prevProps.patient && this.props.instructions.audio_note == null && this.props.patient.audio_note) {
      this.props.addInstructions({ audio_note: this.props.patient.audio_note });
    }

    // Keep the recipe form populated from the saved prescription. The connect
    // provides the right `initialValues`, but redux-form keeps resetting the
    // form to empty on edit (enableReinitialize + keepDirtyOnReinitialize +
    // remounts), so a one-shot fill gets wiped. Mirror how `material` survives
    // below: re-apply via `change` (which marks fields DIRTY, so they are kept)
    // whenever the form is missing content the DB has. It settles after one
    // apply and self-heals if a later reset blanks the form again.
    if (isPatient(this.props.patient)) {
      const lastCorrection = getLastCorrection(this.props.patient);
      const rx = lastCorrection.prescription;
      const fv = (this.props.formValues ?? {}) as Record<string, unknown>;
      const formMissingRecipe =
        rx != null &&
        ((Boolean(rx.comment) && !fv["comment"]) ||
          (rx.arch != null && fv["arch"] == null) ||
          (rx.upper_arch_action != null && fv["upper_arch_action"] == null) ||
          (rx.lower_arch_action != null && fv["lower_arch_action"] == null) ||
          (rx.teeth_level_align != null && fv["teeth_level_align"] == null));
      if (rx != null && formMissingRecipe && !lastCorrection.order_options.can_order_correction) {
        const quantity = isRetainersCourse(this.props.patient.course.course_id) ? getRetainersQuantityFromCourseOptionTag(this.props.patient.course.course_option_tag) : null;
        const values = initializeBaseInstruction({ ...rx, quantity });
        Object.entries(values).forEach(([field, value]) => {
          this.props.changeField("correction", field, value as never);
        });
      }
    }

    if (isPatient(this.props.patient) && this.props.instructions.audio_note != null && !resolveHasSmartPrescriptionFilling(this.props.instructions, this.props.patient)) {
      this.props.addInstructions({ audio_note: null });
    }

    if (isPatient(this.props.patient) && this.props.patient !== prevProps.patient && this.state.selectedMaterial == null) {
      const initialMaterial = getLastCorrection(this.props.patient).prescription?.material ?? null;
      if (initialMaterial != null) {
        this.setState({ selectedMaterial: initialMaterial });
      }
    }

    if (isPatient(this.props.patient)) {
      const hasSmartPrescriptionFilling = resolveHasSmartPrescriptionFilling(this.props.instructions, this.props.patient);
      const storedMaterial = getLastCorrection(this.props.patient).prescription?.material ?? null;
      const currentMaterial = this.props.formValues?.material ?? null;
      if (hasSmartPrescriptionFilling && currentMaterial == null && storedMaterial != null) {
        this.props.changeField("correction", "material", storedMaterial);
      }
    }

    const currentMaterial = this.props.formValues?.material ?? null;
    const previousMaterial = prevProps.formValues?.material ?? null;
    if (currentMaterial != null && currentMaterial !== previousMaterial && currentMaterial !== this.state.selectedMaterial) {
      this.setState({ selectedMaterial: currentMaterial });
    }
  }

  componentWillUnmount() {
    this.props.eraseStateProp("patient");
    window.onbeforeunload = null;
  }

  setDirty<T extends keyof TInstructions>(key: T, data: TInstructions[T]) {
    const patient = this.props.patient as TPatient;

    if (
      data == "" ||
      (key in patient && data !== patient[key as keyof TPatient]) ||
      (key in patient.course && data !== patient.course[key as keyof TPatient["course"]]) ||
      (key in patient.clinic && data !== patient.clinic[key as keyof TPatient["clinic"]])
    ) {
      this.setState({ isDirty: true });
    } else {
      this.setState({ isDirty: false });
    }
  }

  getIsFormDirty(): boolean {
    const { media, media_s3, areFormValuesDirty, areLinksDirty } = this.props;
    const { isDirty } = this.state;

    const isMediaDirty = Object.keys(media).length > 0;
    const isS3MediaDirty = Array.isArray(media_s3.files) && media_s3.files.length > 0;

    return isDirty || areFormValuesDirty || areLinksDirty || isMediaDirty || isS3MediaDirty;
  }

  selectDeepCBCTAnalysis(value: boolean) {
    this.props.addInstructions({ deep_cbct: value });
    this.setDirty("deep_cbct", value);
  }

  addCondition(value: string) {
    this.props.addInstructions({ condition: value });
    this.setDirty("condition", value);
  }

  selectAdditionalServiceTags(tags: AdditionalPaidServiceTag[]) {
    this.props.addInstructions({ additional_service_tags: tags });
    this.setDirty("additional_service_tags", tags);
  }

  renderRx(course_id: CourseId | TNone, disabled = false, beforeFields?: React.ReactNode, fieldsWrapper?: (fields: React.ReactNode) => React.ReactNode) {
    const commentLabelId = course_id != null && isPowerCapCourse(course_id) ? "pat.power_cap.comment" : undefined;
    return <RecipeForm course_id={course_id} disabled={disabled} beforeFields={beforeFields} fieldsWrapper={fieldsWrapper} commentLabelId={commentLabelId} />;
  }

  render() {
    const { submit } = this.state;
    const { user, patient, instructions, formValues: { material } = {}, courseInstallment } = this.props;

    if (!isPatient(patient) || !courseInstallment) {
      return (
        <Layout chrome={!this.props.bareLayout}>
          <Loader />
        </Layout>
      );
    }

    const lastCorrection = getLastCorrection(patient);
    const { full_edit, can_edit_prescription } = lastCorrection.order_options;
    const {
      course_id = patient.course.course_id,
      deep_cbct,
      additional_service_tags = extractAdditionalServiceTags(patient),
      audio_note = patient.audio_note,
      clinic_id = patient.clinic?.clinic_id,
    } = instructions;
    const isUnfilledCase = patient.status == Status.UNFILLED_CASE;
    const disable_on_3d_smile_pro = isUnfilledCase && is3DPROCourse(patient.course.course_id);

    const formIsDirty = !submit && this.getIsFormDirty();
    const showTestPlasticOptions = canOrderTestPlastic(user) && (isUnfilledCase || lastCorrection.test_plastic != null);
    const hasSmartPrescriptionFilling = resolveHasSmartPrescriptionFilling(instructions, patient);
    const hasExistingAudio =
      (audio_note?.uploads?.length ?? 0) > 0 ||
      (audio_note?.files?.length ?? 0) > 0 ||
      Boolean(audio_note?.transcript_text) ||
      audio_note?.transcript_status === "processing" ||
      audio_note?.transcript_status === "ready" ||
      audio_note?.id != null;
    // Смарт-рецепт всегда доступен для ручного заполнения; на время обработки аудио
    // ввод перекрывает модальное окно координатора, поэтому блокировать не нужно.
    const recipeDisabled = false;
    const additionalServicesSection =
      !isNone(course_id) && course_id != null && !isPowerCapCourse(course_id) && courseInstallment[course_id] ? (
        <PatientNewAdditionalServices
          isUpdate
          courseId={course_id}
          showDeepCBCT={!isCBCTIncludedInCourse(courseInstallment, course_id) && resolveShowDeepCBCT(courseInstallment[course_id].deep_cbct != null, true)}
          deepCBCTDisabled={
            !isCBCTServiceAvailableForCourse(courseInstallment, course_id) ||
            (patient.status != Status.UNFILLED_CASE && (patient.deep_cbct || [CO_TAG_FULL_3_STEPS_CBCT, CO_TAG_TEEN_3_STEPS_CBCT].includes(patient.course.course_option_tag)))
          }
          deepCBCTChecked={resolveDeepCBCTChecked(deep_cbct == true, isCBCTIncludedInCourse(courseInstallment, course_id))}
          onDeepCBCTCheckedChange={(checked) => this.selectDeepCBCTAnalysis(checked)}
          additionalServiceTags={additional_service_tags}
          onAdditionalServiceTagsChange={this.selectAdditionalServiceTags}
        />
      ) : null;

    return (
      <Layout chrome={!this.props.bareLayout}>
        <Portlet as="main">
          <PortletTitle iconClassName="icon-book-open" displayRequiredFieldsInfo>
            <FormattedMessage id="BTN_EDIT" />
          </PortletTitle>

          <RouteLeavingGuard when={formIsDirty} shouldBlockNavigation={() => formIsDirty} saveChanges={isUnfilledCase ? this.submitSaveButton : this.submitButton} saveAsADraft={false} />

          <div className="portlet-body form" id="add-patient-form">
            <div className="form-body">
              <div className="alert alert-danger display-hide">
                <button className="close" data-close="alert" />
                <span id="error-msg" />
              </div>

              <div className="row">
                <div className="col-md-5">
                  <PatientUpdateDoctorInfo patient={patient} setDirty={this.setDirty} />
                </div>
              </div>

              {full_edit ? (
                <>
                  <PatientUpdatePatientInfo patient={patient} setDirty={this.setDirty} />
                  <PatientUpdateCourse
                    patient={patient}
                    courseInstallment={courseInstallment}
                    setDirty={this.setDirty}
                    // Под курсом — только с включённым флагом (см. patient_new.tsx).
                    additionalServices={featureFlags.additionalPaidServices ? additionalServicesSection : undefined}
                  />
                  <PatientUpdatePayment patient={patient} courseInstallment={courseInstallment} setDirty={this.setDirty} />
                  {featureFlags.additionalPaidServices ? null : additionalServicesSection}
                  {isCapLikeCourse(course_id) || isAdditionalAlignersCourse(course_id) ? null : (
                    <DiagnosisTextArea defaultValue={patient.diagnosis} onValueChange={(value) => this.addCondition(value)} />
                  )}
                  {isAdditionalAlignersCourse(course_id) || hasSmartPrescriptionFilling || isPowerCapCourse(course_id) ? null : (
                    <MaterialOptions onMaterialChange={(selectedMaterial) => this.setState({ selectedMaterial })} />
                  )}
                  {showTestPlasticOptions ? <TestPlaticOptions value={this.props.instructions.test_plastic} onValueChange={(test_plastic) => this.props.addInstructions({ test_plastic })} /> : null}
                  {hasSmartPrescriptionFilling ? (
                    <>
                      <PatientNewAudioNote
                        audioNote={audio_note}
                        patientId={patient.patient_id}
                        clinicId={clinic_id}
                        forceEditable
                        onChange={(nextAudioNote) => {
                          this.props.addInstructions({ audio_note: nextAudioNote });
                          this.setDirty("audio_note", nextAudioNote);
                        }}
                      />
                      <PatientUpdateFiles patient={patient} showUploadedFiles smartPrescriptionMode />
                      <SmartPrescriptionCoordinator deferInitialFill={hasExistingAudio} enabledOverride={hasSmartPrescriptionFilling} />
                    </>
                  ) : null}
                  {isAdditionalAlignersCourse(course_id) ? <PatientUpdateAdditionalAligners maxStage={patient.original_patient?.steps_count_completed as number} /> : null}
                  {this.renderRx(course_id, recipeDisabled)}
                </>
              ) : null}

              {can_edit_prescription && !full_edit && !isAdditionalAlignersCourse(course_id) && !isPowerCapCourse(course_id) && !hasSmartPrescriptionFilling ? (
                <MaterialOptions onMaterialChange={(selectedMaterial) => this.setState({ selectedMaterial })} />
              ) : null}

              {can_edit_prescription && !full_edit && hasSmartPrescriptionFilling ? (
                <>
                  <PatientNewAudioNote
                    audioNote={audio_note}
                    patientId={patient.patient_id}
                    clinicId={clinic_id}
                    forceEditable
                    onChange={(nextAudioNote) => {
                      this.props.addInstructions({ audio_note: nextAudioNote });
                      this.setDirty("audio_note", nextAudioNote);
                    }}
                  />
                  <PatientUpdateFiles patient={patient} showUploadedFiles smartPrescriptionMode />
                  <SmartPrescriptionCoordinator deferInitialFill={hasExistingAudio} enabledOverride={hasSmartPrescriptionFilling} />
                </>
              ) : null}

              {isPowerCapCourse(course_id) ? (
                <PowerCapFileUpload required isVisible />
              ) : (isRetainersCourse(course_id) ? material == Material.SCAN : !isAdditionalAlignersCourse(course_id) && !hasSmartPrescriptionFilling) ? (
                <PatientUpdateFiles patient={patient} required={isRetainersCourse(course_id) ? material == Material.SCAN : false} showUploadedFiles />
              ) : null}

              {isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id) || isPowerCapCourse(course_id) || hasSmartPrescriptionFilling ? null : <PatientUpdateUploadCT showUploadedFiles />}

              {isUnfilledCase && !isRetainersCourse(course_id) && !isAdditionalAlignersCourse(course_id) && !isPowerCapCourse(course_id) && !hasSmartPrescriptionFilling ? (
                <PatientNewImages patient_id={patient.patient_id} />
              ) : null}

              {can_edit_prescription && !full_edit && !isAdditionalAlignersCourse(course_id) ? this.renderRx(course_id, recipeDisabled) : null}
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-border-t tw-border-[#e7ecf1] tw-py-5 sm:tw-gap-2">
              <LoadingButton
                id="submit-pacient-btn"
                type="button"
                variant="primary"
                size="xl"
                className="tw-w-full sm:tw-max-w-[180px]"
                disabled={disable_on_3d_smile_pro || this.state.isDraftPending}
                onClick={() => this.submitButton()}
                isLoading={this.state.isSubmitPending}
              >
                <FormattedMessage id={isUnfilledCase ? "BTN_SUBMIT_NEW" : "BTN_SAVE"} />
              </LoadingButton>

              {isUnfilledCase && !isAdditionalAlignersCourse(course_id) ? (
                <LoadingButton
                  id="save-pacient-btn"
                  type="button"
                  variant="secondary"
                  size="xl"
                  className="tw-w-full sm:tw-max-w-[180px]"
                  disabled={this.state.isSubmitPending}
                  onClick={() => this.submitSaveButton()}
                  isLoading={this.state.isDraftPending}
                >
                  <FormattedMessage id="BTN_SAVE_DRAFT" />
                </LoadingButton>
              ) : null}
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientUpdate));
